import { Box, Grid, useMediaQuery, useTheme, Typography } from '@mui/material';
import PageHeader from '../components/Page/PageHeader';
import { articleCardLocation } from '../utilities/gtm';
import CategoryFiltersList from '../components/Category/CategoryFilters/List/CategoryFiltersList';
import { getPrimaryCategories } from '../utilities/Categories';
import { useMemo } from 'react';
import { useArticles, UseArticlesResult } from '../hooks/useArticles';
import { apiStates, CONTENT_TYPE } from '../types/types';
import { useTranslation } from 'react-i18next';
import NewsArticleList from '../components/News/NewsArticleList';
import NewsArticleListSkeleton from '../components/News/NewsArticleListSkeleton';
import CategoryFiltersDrawer from '../components/Category/CategoryFilters/Drawer/CategoryFiltersDrawer';
import Pagination from '../components/Page/Pagination';
import useDocumentTitle from '../hooks/useDocumentTitle';
import ErrorView from '../components/ErrorViews/ErrorView';
import useContentSearchParams from '../hooks/useContentSearchParams';

const newsArticleLimit = 10;

export default function NewsIndexPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const minimizeCategories = useMediaQuery(theme.breakpoints.down('md'));
  const { category: activeCategory, updateCategory } = useContentSearchParams();

  const pageTitle = useMemo(() => {
    let title = t('news.title');

    if (activeCategory) {
      title = `${t('news.title')}: ${activeCategory}`;
    }
    return title;
  }, [t, activeCategory]);

  useDocumentTitle(pageTitle);

  const {
    articles: news,
    state: apiState,
    apiError,
    totalArticleCount,
    hasNextPage,
    fetchNextPage,
  }: UseArticlesResult = useArticles({
    limit: newsArticleLimit,
    category: activeCategory,
    contentType: 'Uutinen',
  });

  const categories = useMemo(() => {
    return getPrimaryCategories(CONTENT_TYPE.LegalNews).sort((a, b) => {
      return a.localeCompare(b);
    });
  }, []);

  return (
    <Box>
      <PageHeader
        title={pageTitle}
        titleItem={
          minimizeCategories ? (
            <CategoryFiltersDrawer
              activeCategory={activeCategory}
              categories={categories}
              onSelectCategory={updateCategory}
            />
          ) : undefined
        }
      />

      {apiState === apiStates.HAS_ERROR && <ErrorView apiError={apiError} />}

      {apiState !== apiStates.HAS_ERROR && (
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            {!minimizeCategories && (
              <Box
                component='aside'
                sx={{
                  paddingTop: 1,
                  paddingRight: 2,
                }}
              >
                <CategoryFiltersList
                  activeCategory={activeCategory}
                  categories={categories}
                  onSelectCategory={updateCategory}
                />
              </Box>
            )}
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 9,
              lg: 7,
            }}
          >
            {apiState === apiStates.FETCHING && (
              <NewsArticleListSkeleton limit={newsArticleLimit} />
            )}
            {apiState === apiStates.READY && (
              <>
                {news.length > 0 && (
                  <>
                    <NewsArticleList
                      articles={news}
                      location={articleCardLocation.MAIN_CONTENT}
                    />
                    <Box
                      sx={{
                        marginTop: 3,
                      }}
                    >
                      <Pagination
                        loadedArticleCount={news.length}
                        totalArticleCount={totalArticleCount}
                        hasNextPage={hasNextPage}
                        fetchNextPage={fetchNextPage}
                      />
                    </Box>
                  </>
                )}
                {news.length === 0 && (
                  <Typography
                    sx={{
                      marginBottom: 6,
                    }}
                  >
                    {t('articles.noContent')}
                  </Typography>
                )}
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
